import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Icon,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion, useInView } from 'framer-motion';
import React, { useRef } from 'react';
import {
  FaCogs,
  FaGraduationCap,
  FaHeadset,
  FaMapMarkedAlt,
  FaPhoneAlt,
} from 'react-icons/fa/index.js';

const MotionBox = motion(Box);

const STEPS = [
  { icon: FaPhoneAlt, label: 'Discovery', color: 'blue.400', desc: 'We learn your process.' },
  { icon: FaMapMarkedAlt, label: 'Workflow Map', color: 'purple.400', desc: 'You approve before we build.' },
  { icon: FaCogs, label: 'Implementation', color: 'orange.400', desc: 'We build and test everything.' },
  { icon: FaGraduationCap, label: 'Training', color: 'green.400', desc: 'Your team learns the system.' },
  { icon: FaHeadset, label: 'Support', color: 'teal.400', desc: '30 days of live monitoring.' },
];

const HowItWorks = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const bg = useColorModeValue('gray.900', 'gray.950');
  const cardBg = useColorModeValue('gray.800', 'gray.800');
  const textColor = useColorModeValue('gray.400', 'gray.400');

  return (
    <Box bg={bg} py={{ base: 16, md: 24 }} ref={ref}>
      <Container maxW="container.xl">
        <VStack spacing={4} mb={16} textAlign="center">
          <Badge colorScheme="blue" fontSize="sm" px={4} py={1} borderRadius="full">
            The Process
          </Badge>
          <Heading
            as="h2"
            fontSize={{ base: '2xl', md: '4xl' }}
            fontWeight="extrabold"
            color="white"
            letterSpacing="-0.03em"
          >
            Five steps. Fixed scope. No surprises.
          </Heading>
          <Text fontSize="lg" color={textColor} maxW="xl">
            Every engagement follows the same proven delivery process.
            You know exactly what happens next at every stage.
          </Text>
        </VStack>

        {/* Step timeline */}
        <Flex
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'flex-start', md: 'flex-start' }}
          justify="center"
          gap={{ base: 0, md: 0 }}
          position="relative"
        >
          {/* Connector line (desktop only) */}
          <Box
            display={{ base: 'none', md: 'block' }}
            position="absolute"
            top="32px"
            left="10%"
            right="10%"
            h="2px"
            bg="gray.700"
            zIndex={0}
          />

          {STEPS.map((step, i) => (
            <MotionBox
              key={step.label}
              flex="1"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.15 } as any}
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
              px={4}
              py={{ base: 4, md: 0 }}
              position="relative"
              zIndex={1}
            >
              {/* Icon circle */}
              <Box
                w={16} h={16}
                borderRadius="full"
                bg={cardBg}
                border="2px solid"
                borderColor={step.color}
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={4}
                boxShadow={`0 0 20px ${step.color}40`}
              >
                <Icon as={step.icon} boxSize={6} color={step.color} />
              </Box>
              <Text fontWeight="bold" color="white" fontSize="sm" mb={1}>
                {step.label}
              </Text>
              <Text fontSize="xs" color={textColor} maxW="120px">
                {step.desc}
              </Text>
            </MotionBox>
          ))}
        </Flex>

        {/* CTA */}
        <VStack mt={14} spacing={4}>
          <Button
            as="a"
            href="/how-it-works"
            variant="outline"
            colorScheme="blue"
            size="lg"
            px={10}
            _hover={{ bg: 'blue.900', transform: 'translateY(-2px)' }}
            transition="all 0.2s"
          >
            See the Full Process →
          </Button>
        </VStack>
      </Container>
    </Box>
  );
};

export default HowItWorks;
